import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import Constants from "../../../../constants/Index";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { ButtonV2, RemoveButtonV2 } from "../../../../components/FormControlsV2";
import { OnboardingFooterV2 } from "../../../../components/OnboardingV2/OnboardingFooterV2";
import { AreaFormV2, AreaFormValueV2 } from "../../../../components/FormsV2";
import {
  fromAreaFormValue,
  toAreaFormValue,
  toFarmOptions,
  toUserOptions,
} from "../adapters/propertyForms";
import { persistPayload } from "../adapters/payload";
import { hasErrors, validateV2, ValidationErrorsV2 } from "../adapters/validation";
import { generateuuid } from "../../../../utils/uuid/generate";
import { DrawOnMapSlotV2 } from "../../../../components/OnboardingV2/DrawOnMapSlotV2";
import { calculateHectares } from "../../../../utils/geolib/calculateHectares";

const areaSchema = yup.object().shape({
  farm: yup.object().nullable().required("Fazenda é necessária."),
  name: yup.string().required("Campo obrigatório não informado."),
  initials: yup.string().max(3, "tamanho deve ser entre 1 e 3").required("Campo obrigatório não informado."),
  acreage: yup.string().required("Campo obrigatório não informado."),
  indoor_grazing_time: yup.object().nullable().required("Tempo máximo COM gado é necessário."),
  outdoor_grazing_time: yup.object().nullable().required("Tempo máximo SEM gado é necessário."),
  manager_user_id: yup.object().nullable().required("Campo obrigatório não informado."),
  map_coords: yup.array().min(1, Constants.MESSAGE_MAPPING_NOT_FOUND),
});

function createAreaBase(farm: any): AreaFormValueV2 {
  return {
    uuid: generateuuid(),
    farm,
    name: "",
    initials: "",
    acreage: "0",
    indoor_grazing_time: null,
    outdoor_grazing_time: null,
    manager_user_id: null,
    map_coords: [],
  };
}

type CreateAreasByFarmV2Props = {
  farmId: string;
};

export function CreateAreasByFarmV2({ farmId }: CreateAreasByFarmV2Props) {
  const { payload, setPayload, setBlockNextStep, apiFieldErrors, setApiFieldErrors, clearStepError } = useOnboarding();
  const farmOptions = useMemo(() => toFarmOptions(payload), [payload]);
  const activeFarm = farmOptions.find((farm) => String(farm.value) === String(farmId)) || null;
  const managers = toUserOptions(payload, Constants.PROFILE_CODE_CAPATAZ);
  const dayOptions = Constants.DAYS_OPTIONS.map((option) => ({ label: option.label, value: option.value }));
  const [areas, setAreas] = useState<AreaFormValueV2[]>([]);
  const [errors, setErrors] = useState<Record<string, ValidationErrorsV2>>({});
  const [showFullValidation, setShowFullValidation] = useState(false);

  async function validateAreas(areasToValidate = areas, fullValidation = showFullValidation) {
    const validationEntries = await Promise.all(
      areasToValidate.map(async (area, index) => {
        const validation = fullValidation ? await validateV2(areaSchema, area) : {};
        const sameInitials = areasToValidate.filter((item) => item.initials && item.initials === area.initials).length;
        if (sameInitials > 1) validation.initials = "Esta sigla já está sendo utilizada.";
        return [String(index), validation] as const;
      })
    );
    const validation = Object.fromEntries(validationEntries);
    setErrors(validation);
    const invalid = Object.values(validation).some(hasErrors);
    const hasApiErrors = hasAreaApiErrors(areasToValidate);
    setBlockNextStep(invalid || hasApiErrors);
    if (!invalid && !hasApiErrors) clearAreaStepError();
    return !invalid && !hasApiErrors;
  }

  function updateArea(index: number, value: AreaFormValueV2) {
    const changedFields = Object.keys(value).filter(
      (field) => areas[index]?.[field as keyof AreaFormValueV2] !== value[field as keyof AreaFormValueV2]
    );
    const nextAreas = areas.map((area, areaIndex) => (areaIndex === index ? value : area));
    setAreas(nextAreas);
    clearApiAreaErrors(index, changedFields);
    validateAreas(nextAreas);
  }

  function mapApiAreaField(field: string) {
    const fieldName = field.replace(/^areas\[\d+\]\./, "");
    const fieldMap = {
      ownerUserId: "manager_user_id",
      owner_user_id: "manager_user_id",
      indoorGrazingTime: "indoor_grazing_time",
      indoor_grazing_time: "indoor_grazing_time",
      outdoorGrazingTime: "outdoor_grazing_time",
      outdoor_grazing_time: "outdoor_grazing_time",
      mapCoords: "map_coords",
      map_coords: "map_coords",
    } as Record<string, string>;
    return fieldMap[fieldName] || fieldName;
  }

  function getActiveFarmStep() {
    const farmIndex = (payload?.farms || []).findIndex((farm: any) => String(farm.uuid || farm.id) === String(activeFarm?.value));
    return farmIndex >= 0 ? farmIndex + 2 : null;
  }

  function clearAreaStepError() {
    const section = getActiveFarmStep();
    if (!activeFarm || !section) return;

    clearStepError(section, 0);
  }

  function clearApiAreaErrors(index: number, changedFields: string[]) {
    if (!changedFields.length) return;

    const areaUuid = areas[index]?.uuid;
    clearApiAreaErrorsByUuid(areaUuid, changedFields);
  }

  function clearApiAreaErrorsByUuid(areaUuid?: string, changedFields?: string[]) {
    if (!areaUuid) return;

    setApiFieldErrors((current) => {
      const next = { ...current };
      Object.keys(next).forEach((field) => {
        const match = field.match(/^areas\[(\d+)\]\.(.+)$/);
        if (!match) return;

        const globalArea = payload?.areas?.[Number(match[1])];
        if (String(globalArea?.uuid) !== String(areaUuid)) return;
        if (!changedFields || changedFields.includes(mapApiAreaField(field))) delete next[field];
      });
      return next;
    });
  }

  function getAreaApiErrors(index: number) {
    return Object.entries(apiFieldErrors).reduce((fieldErrors, [field, message]) => {
      const match = field.match(/^areas\[(\d+)\]\.(.+)$/);
      if (!match) return fieldErrors;

      const globalArea = payload?.areas?.[Number(match[1])];
      if (!globalArea || String(globalArea.farm_id) !== String(activeFarm?.value)) return fieldErrors;

      const localIndex = areas.findIndex((area) => String(area.uuid) === String(globalArea.uuid));
      if (localIndex !== index) return fieldErrors;

      fieldErrors[mapApiAreaField(field)] = message;
      return fieldErrors;
    }, {} as ValidationErrorsV2);
  }

  function getAreaErrors(index: number) {
    return {
      ...getAreaApiErrors(index),
      ...(errors[String(index)] || {}),
    };
  }

  function hasAreaApiErrors(areasToMatch = areas) {
    return Object.keys(apiFieldErrors).some((field) => {
      const match = field.match(/^areas\[(\d+)\]\./);
      if (!match) return false;

      const globalArea = payload?.areas?.[Number(match[1])];
      if (!globalArea || String(globalArea.farm_id) !== String(activeFarm?.value)) return false;

      if (!areasToMatch.length) return true;
      return areasToMatch.some((area) => String(area.uuid) === String(globalArea.uuid));
    });
  }

  function updateAreaMapCoords(index: number, area: AreaFormValueV2, map_coords: any[]) {
    const calculatedAcreage = map_coords.length >= 3 ? calculateHectares(map_coords) : area.acreage;
    updateArea(index, { ...area, map_coords, acreage: calculatedAcreage });
  }

  function addArea() {
    if (!activeFarm) return;
    const nextAreas = [...areas, createAreaBase(activeFarm)];
    setAreas(nextAreas);
    validateAreas(nextAreas);
  }

  function removeAreaCascade(area: AreaFormValueV2) {
    const areaId = area.uuid || area.id;
    if (!areaId) return;

    setPayload((current) => {
      const next = {
        ...current,
        areas: (current?.areas || []).filter((currentArea: any) => String(currentArea.uuid || currentArea.id) !== String(areaId)),
        micro_areas: (current?.micro_areas || []).filter((microArea: any) => String(microArea.area_id) !== String(areaId)),
      };
      persistPayload(next);
      return next as any;
    });
  }

  function removeArea(index: number) {
    if (areas.length <= 1) return;

    clearApiAreaErrorsByUuid(areas[index]?.uuid);
    removeAreaCascade(areas[index]);
    const nextAreas = areas.filter((_, areaIndex) => areaIndex !== index);
    setAreas(nextAreas);
    validateAreas(nextAreas);
  }

  function getStaticAreas(index: number) {
    return areas
      .filter((_, areaIndex) => areaIndex !== index)
      .map((area) => area.map_coords)
      .filter((coords) => coords && coords.length > 0) as any;
  }

  async function handleNext() {
    if (!activeFarm) return false;

    setShowFullValidation(true);
    const isValid = await validateAreas(areas, true);
    if (!isValid) return false;

    const farmAreas = areas.map((area) => fromAreaFormValue(area, String(activeFarm.value)));
    setPayload((current) => {
      const next = {
        ...current,
        areas: (current?.areas || []).filter((area: any) => String(area.farm_id) !== String(activeFarm.value)).concat(farmAreas),
      };
      persistPayload(next);
      return next as any;
    });
    return true;
  }

  useEffect(() => {
    if (!activeFarm) return;
    const existingAreas = (payload?.areas || []).filter((area: any) => String(area.farm_id) === String(activeFarm.value));
    setAreas(existingAreas.length ? existingAreas.map((area: any) => toAreaFormValue(area, activeFarm, managers)) : [createAreaBase(activeFarm)]);
  }, [activeFarm?.value, payload?.areas?.length, managers.length]);

  useEffect(() => {
    const invalid = Object.values(errors).some(hasErrors);
    const hasApiErrors = hasAreaApiErrors();
    setBlockNextStep(invalid || hasApiErrors);
    if (!invalid && !hasApiErrors) clearAreaStepError();
  }, [activeFarm?.value, apiFieldErrors, areas.length, errors, payload?.areas?.length, setBlockNextStep]);

  return (
    <>
      <main>
        <div className="onboarding-v2-form-page">
          {areas.map((area, index) => (
            <div className="onboarding-v2-list-card" key={area.uuid || index}>
              <AreaFormV2
                value={area}
                farms={farmOptions}
                managers={managers}
                dayOptions={dayOptions}
                initialsPrefix={activeFarm?.info?.initials ? `${activeFarm.info.initials}-` : ""}
                errors={getAreaErrors(index)}
                cardTitle={`Retiro ${index + 1}`}
                cardDescription={
                  area.name
                    ? `${area.name}${area.initials
                      ? ` (${activeFarm?.info?.initials ? `${activeFarm.info.initials}-` : ""}${area.initials})`
                      : ""
                    }`
                    : area.initials
                      ? `${activeFarm?.info?.initials ? `${activeFarm.info.initials}-` : ""}${area.initials}`
                      : undefined
                }
                disabledFields={["farm", "acreage"]}
                showActions={false}
                headerActions={
                  areas.length > 1 ? (
                    <RemoveButtonV2 onClick={() => removeArea(index)} />
                  ) : undefined
                }
                mapSlot={
                  <DrawOnMapSlotV2
                    value={area.map_coords as any}
                    fitCoords={activeFarm?.info?.map_coords || []}
                    staticsElements={getStaticAreas(index)}
                    initialsLabel={`${activeFarm?.info?.initials || ""}-${area.initials || index + 1}`}
                    editable={!!activeFarm}
                    error={getAreaErrors(index).map_coords}
                    onChange={(map_coords) => updateAreaMapCoords(index, area, map_coords)}
                  />
                }
                onBlur={() => validateAreas()}
                onChange={(value) => updateArea(index, value)}
              />
            </div>
          ))}
          <div className="onboarding-v2-page-actions">
            <ButtonV2 onClick={addArea}>+ ADICIONAR RETIRO</ButtonV2>
          </div>
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={handleNext} />
    </>
  );
}
